import { useCallback, useMemo } from 'react';
import { useMobileDetection } from '@/hooks/useMobileDetection';
import { LeftPanel } from './LeftPanel';
import { LazyMapView } from '@/components/map/LazyMapView';
import { UnifiedInfoDialog, type InfoData } from '@/components/wards/UnifiedInfoDialog';
import { useWardStore } from '@/stores/wardStore';
import { useUIStore } from '@/stores/uiStore';
import type { WardInfo } from '@/types/ward';

export function AppLayout() {
  const { isMobile } = useMobileDetection();
  const selectedWard = useWardStore((state) => state.selectedWard);
  const selectedPlaceAttributes = useWardStore((state) => state.selectedPlaceAttributes);
  const selectWard = useWardStore((state) => state.selectWard);
  const clearSelection = useWardStore((state) => state.clearSelection);
  const isPanelOpen = useUIStore((state) => state.isPanelOpen);

  const handleWardSelect = useCallback((ward: WardInfo) => {
    selectWard(ward);
  }, [selectWard]);

  // Prepare unified info data
  const infoData: InfoData = useMemo(() => {
    if (selectedWard) {
      return { type: 'ward', data: selectedWard };
    } else if (selectedPlaceAttributes) {
      // Create a mock Graphic object for place data
      const placeGraphic = {
        attributes: selectedPlaceAttributes,
      } as __esri.Graphic;
      return { type: 'place', data: placeGraphic };
    }
    return null;
  }, [selectedWard, selectedPlaceAttributes]);

  const handleInfoClose = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  return (
    <div className="h-screen w-screen overflow-hidden bg-gray-50">
      {/* Main content */}
      <div className="relative flex h-screen">
        {/* Left Panel */}
        <LeftPanel />

        {/* Map Container */}
        <main
          className={`relative flex-1 transition-all duration-300 ${
            !isMobile && isPanelOpen ? 'ml-0' : 'ml-0'
          }`}
        >
          <LazyMapView
            onWardSelect={handleWardSelect}
            selectedWardId={selectedWard?.objectid}
            className="h-full w-full"
          />
        </main>

        {/* Unified Information Display */}
        <UnifiedInfoDialog info={infoData} onClose={handleInfoClose} />
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white shadow-lg">
          {/* Mobile navigation will be added here */}
        </div>
      )}
    </div>
  );
}
